/**
 * ============================================================================
 * [P0-01] Type Definitions
 *
 * 500자 글 작성기 - 공통 타입 정의
 * ============================================================================
 */

// ============================================================================
// 사용자 관련 타입
// ============================================================================
export interface User {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
}

// ============================================================================
// 텍스트/글 관련 타입
// ============================================================================
export interface SavedText {
  id: string
  content: string
  title: string
  type: 'edit' | 'reference' | 'script'
  topic?: string
  source?: string
  snsPlatforms?: string[]
  referenceType?: 'structure' | 'idea' | 'unspecified'
  createdAt: Date | string
  updatedAt: Date | string
  userId: string
  charLimit?: number
  charCount: number  // [P3-02] 트래킹을 위한 글자수 필드 추가
  isFavorite?: boolean  // [P3-01] 즐겨찾기 상태
  isUsed?: boolean      // 레퍼런스 수동 사용 표시
  isDeleted?: boolean   // [P5-02] 휴지통 - 소프트 삭제 플래그
  deletedAt?: Date | string  // [P5-02] 휴지통 - 삭제 시간
  linkedReferences?: string[]  // 참고 레퍼런스 ID 목록
  analysisSummary?: ReferenceAnalysisSummary  // 레퍼런스 분석 요약(optional)
}

// ============================================================================
// 트래킹/메트릭 관련 타입
// ============================================================================
export interface TrackingPost {
  id: string
  title: string
  content?: string             // 포스트 내용 (선택)
  platform: string             // SNS 플랫폼
  url?: string                 // 포스트 URL
  createdAt: Date | string     // 생성일
  updatedAt: Date | string     // 수정일
  userId: string               // 사용자 ID
  isFavorite?: boolean         // 즐겨찾기
  isActive?: boolean           // 활성 상태 (트래킹 중)
  metrics: TrackingMetric[]    // 메트릭 기록들
}

export interface TrackingMetric {
  id: string                   // 메트릭 ID
  date: string                 // 측정 날짜 (YYYY-MM-DD)
  views: number                // 조회수
  likes: number                // 좋아요
  comments: number             // 댓글
  shares: number               // 공유
  follows?: number             // 팔로우 (Threads 전용)
  saves?: number               // 저장 (Instagram 전용)
  notes?: string               // [P1-01] 메트릭 메모 추가
}

// ============================================================================
// 트래킹 필터 관련 타입
// ============================================================================
export type TrackingSortOption =
  | 'updatedDesc' | 'updatedAsc'
  | 'viewsDesc' | 'viewsAsc'
  | 'likesDesc' | 'likesAsc'
  | 'commentsDesc' | 'commentsAsc'
  | 'sharesDesc' | 'sharesAsc'
  | 'followsDesc' | 'followsAsc'
  | 'entriesDesc' | 'entriesAsc'
  | 'favoritesFirst'

export type TrackingStatusFilter = 'all' | 'active' | 'inactive' | 'hasData' | 'noData'

export interface TrackingFilterState {
  search: string                    // 검색어
  sortBy: TrackingSortOption        // 정렬 옵션
  status: TrackingStatusFilter      // 상태 필터
  dateFrom?: string                 // 업데이트 날짜 from (YYYY-MM-DD)
  dateTo?: string                   // 업데이트 날짜 to (YYYY-MM-DD)
  minViews?: number                 // 조회수 최소
  maxViews?: number                 // 조회수 최대
  minLikes?: number                 // 좋아요 최소
  maxLikes?: number                 // 좋아요 최대
  minComments?: number              // 댓글 최소
  maxComments?: number              // 댓글 최대
  minShares?: number                // 공유 최소
  maxShares?: number                // 공유 최대
  minFollows?: number               // 팔로우 최소
  maxFollows?: number               // 팔로우 최대
}

// ============================================================================
// SNS 플랫폼 타입
// ============================================================================
export interface SNSPlatform {
  id: string
  name: string
  icon: string
}

// ============================================================================
// [P3-04] URL 링크 타입
// ============================================================================
export interface UrlLink {
  id: string
  name: string           // 서비스 명칭 (표시용)
  title?: string         // 이전 호환성 유지
  description?: string   // 설명 (선택)
  url: string
  category?: string
  icon?: string          // 아이콘 (이모지)
  order?: number         // 정렬 순서 (드래그 앤 드롭용)
  createdAt: Date | string
}

// URLLink 별칭 (대문자 L 호환성)
export type URLLink = UrlLink

// ============================================================================
// [P3-03] 스크립트 타입
// ============================================================================
export interface Script {
  id: string
  name: string
  content: string
  variables: string[]

  // [P1-01] 카테고리 시스템 - 스크립트 분류용
  category?: string

  // [P1-01] LLM 메타데이터 - AI 모델 정보 저장
  llmModel?: string        // 모델명 (예: 'gpt-4', 'claude-3')
  llmModelType?: string    // 모델 유형 (예: 'chat', 'completion')

  // [P1-01] 순서 관리 - 드래그앤드롭 정렬용
  order?: number

  // [SC-01] 스크립트 모음 - 완성/연재 메타데이터
  completedAt?: Date | string  // 완성 선언 시각. 부재 = 미완성 (null 금지)
  seriesId?: string            // 연재 제목 문자열 (별도 시리즈 문서 없음)
  episode?: number             // 연재 내 회차. 내부 소수 허용, UI는 정수만

  createdAt: Date | string
  updatedAt: Date | string
}

// ============================================================================
// 필터 상태 타입
// ============================================================================
export interface FilterState {
  search: string
  type: 'all' | 'edit' | 'edit-used' | 'reference' | 'reference-used' | 'reference-unused' | 'script'
  sortBy: 'newest' | 'oldest' | 'title' | 'charCount'
  topic?: string  // [P5-03] 주제 필터
  showFavorites?: boolean  // [P3-01] 즐겨찾기만 보기 필터
  referenceType?: 'all' | 'structure' | 'idea'  // 레퍼런스 유형 필터 (구조/아이디어)
}

// ============================================================================
// 탭 타입
// ============================================================================
export type TabType = 'writing' | 'saved' | 'tracking' | 'management' | 'urllink' | 'backup'

// ============================================================================
// [P1-02] 레퍼런스 관련 타입
// ============================================================================
export type ReferenceSourceType = 'saved' | 'tracking'

export interface ReferenceItem {
  id: string
  title: string
  content: string
  sourceType: ReferenceSourceType
  category?: string
  createdAt: Date | string
  // 트래킹 전용 필드
  platform?: string
  metrics?: {
    views: number
    likes: number
    comments: number
  }
  performanceScore?: number
  engagementRate?: number
}

// ============================================================================
// [P0-01] 레퍼런스 분석 타입
// ============================================================================
export const REFERENCE_ANALYSIS_SCORE_KEYS = [
  'structure',
  'topic',
  'hook',
  'emotion',
  'utility',
  'trust',
  'cta',
] as const

export type ReferenceAnalysisScoreKey = typeof REFERENCE_ANALYSIS_SCORE_KEYS[number]

export type ReferenceAnalysisPromptVersion = 'reference-analysis-v1'

// ----------------------------------------------------------------------------
// [v2] 레퍼런스 분석 v2 리터럴/유니온 (가산 전용 — promptVersion은 v1 동결)
// schemaVersion이 실제 버전 축이며, promptVersion은 게이트 통과용 v1 리터럴 유지.
// ----------------------------------------------------------------------------
export type ReferenceAnalysisSchemaVersion = 'reference-analysis-v2'
export type ReferenceAnalysisTargetMode = 'reference' | 'draft'
export type ReferenceEvidenceType = 'quote' | 'metric' | 'inference' | 'assumption'
/** v2 결과 상태. hook의 ReferenceAnalysisStatus(흐름 상태)와 의도적으로 별개 이름. */
export type ReferenceAnalysisResultStatus = 'ok' | 'degraded' | 'insufficient_input'
export type ReferenceDataQuality = 'high' | 'medium' | 'low'
export type OrdinalBand = '상' | '중' | '하'

export const REFERENCE_ANALYSIS_PANEL_SIZE = 1000 as const
export const LEGACY_REFERENCE_ANALYSIS_PANEL_SIZE = 500 as const
export type ReferenceAnalysisPanelSize =
  | typeof REFERENCE_ANALYSIS_PANEL_SIZE
  | typeof LEGACY_REFERENCE_ANALYSIS_PANEL_SIZE

export interface ReferenceAnalysisScore {
  key: ReferenceAnalysisScoreKey
  label: string
  score: number
  evidence: string
  reusablePattern: string
  /** [v2] 근거 유형(가산). evidence 자체는 v1 필수로 유지. */
  evidenceType?: ReferenceEvidenceType
}

export interface ReferenceAnalysisResult {
  // --- v1 코어 (검증 게이트가 강제, 절대 동결) ---
  id: string
  sourceReferenceId: string
  sourceType: ReferenceSourceType
  promptVersion: ReferenceAnalysisPromptVersion
  simulatedPanelSize: typeof REFERENCE_ANALYSIS_PANEL_SIZE
  scores: Record<ReferenceAnalysisScoreKey, number>
  winningFactors: ReferenceAnalysisScore[]
  extractedTopics: string[]
  extractedStructures: string[]
  extractedIdeas: string[]
  cautions: string[]
  createdAt: string

  // --- v2 가산 필드 (전부 옵셔널, 비검증/비throw, 휘발 result + 아티팩트 전용) ---
  schemaVersion?: ReferenceAnalysisSchemaVersion
  targetMode?: ReferenceAnalysisTargetMode
  status?: ReferenceAnalysisResultStatus
  statusReason?: string
  dataQuality?: ReferenceDataQuality
  sensitiveDomain?: string

  panelLabel?: string
  personaCount?: number
  narratedPersonaCount?: number
  archetypeCount?: number
  extrapolationNote?: string
  scaleLegend?: ScaleLegend
  configEcho?: ConfigEcho

  goalAlignedScore?: number
  goalWeights?: GoalWeights
  hookAnalysis?: HookAnalysis
  successDimensions?: SuccessDimension[]

  audienceProfile?: AudienceProfile
  segmentBreakdown?: SegmentBreakdownItem[]
  personaScores?: PersonaScores
  personaCards?: PersonaCard[]
  funnelPrediction?: FunnelPrediction
  topLists?: TopLists

  expertPanel?: ExpertPanelItem[]
  redteamVotes?: RedteamVote[]
  riskAdjustedVerdict?: RiskAdjustedVerdict

  attributionCaveats?: string[]
  improvements?: string[]
  audienceDrift?: AudienceDrift
  goalLeverGap?: string[]

  methodologyNote?: string
  limitations?: string

  /** 코어/타입드 v2 외의 미지 키를 verbatim 보존(파서 passthrough). */
  extras?: Record<string, unknown>
}

// ----------------------------------------------------------------------------
// [v2] 옵셔널 서브 인터페이스 (전부 비검증·비throw 대상; 필드도 대부분 옵셔널)
// ----------------------------------------------------------------------------
/** 그룹별 스케일 범례. 전역 단일 scoreScale 플래그를 대체. */
export interface ScaleLegend {
  legacy?: string
  persona?: string
  intent?: string
}

/** PRIMARY_GOAL에서 파생한 7축 가중치 echo(감사용). */
export type GoalWeights = Partial<Record<ReferenceAnalysisScoreKey, number>>

export interface ConfigEcho {
  targetMode?: string
  platform?: string
  category?: string
  primaryGoal?: string
  metrics?: { views?: number; likes?: number; comments?: number }
  personaCount?: number
  outputMode?: string
  mustIncludeSegments?: string[]
  language?: string
  scaleLegend?: ScaleLegend
  reportPath?: string
  contentTruncated?: boolean
}

/** SNS 후킹 GATE (7축과 별도, 집계 직전 독립 게이트). scrollStopScore는 0~10. */
export interface HookAnalysis {
  firstLine?: string
  foldPoint?: string
  /**
   * 개방형 문자열. 권장 9유형:
   * 호기심갭 | 숫자충격 | 역설 | 공감통증 | 선언 | 질문 | 스토리오프닝 | 반전예고 | no-clear-hook
   */
  hookType?: string
  scrollStopScore?: number
  whyStopsOrFails?: string
}

/** 성공차원(오디언스적합/리텐션/CTA마찰 등). winningFactors와 별개 배열. */
export interface SuccessDimension {
  name: string
  band?: OrdinalBand
  rationale?: string
  evidenceType?: ReferenceEvidenceType
}

/** 페르소나 0~10 점수 그룹(레거시 0~100과 절대 혼합 금지). */
export interface PersonaScores {
  satisfaction?: number
  trust?: number
  demandFit?: number
}

export interface SegmentBreakdownItem {
  segmentId?: string
  label?: string
  cellId?: string
  ratioPct?: number
  avgScores?: PersonaScores
  intentPct?: number
}

export interface AudienceProfile {
  primary?: string
  secondary?: string
  antiAudience?: string
  oneLineReaderDef?: string
}

export interface PersonaProfile {
  age?: number
  gender?: string
  region?: string
  job?: string
  digitalFluency?: string
  scrollBehavior?: string
  /** 개방형(cold|warm 분포축). */
  exposureContext?: string
  authorRelation?: string
  aiFatigue?: string
  saveShareHabit?: string
  coreGoal?: string
  fearResistance?: string
  decisionStyle?: string
  trustSensitivity?: string
  skepticism?: string
  selfIntro?: string
}

export interface PersonaResponse {
  firstImpression?: string
  trust?: number
  comprehension?: number
  relevance?: number
  gotVsWanted?: string
  liked?: string
  disliked?: string
  frictionToAct?: string
  behaviorPrediction?: string[]
  satisfaction?: number
  trustScore?: number
  demandFit?: number
  intentPct?: number
  reason?: string
}

export interface PersonaCard {
  id: string
  cellId?: string
  name?: string
  segment?: string
  profile?: PersonaProfile
  response?: PersonaResponse
}

export interface ThumbStop {
  band?: OrdinalBand
  passProb?: number
}

export interface FunnelStage {
  stage?: string
  band?: OrdinalBand
  dropPoint?: string
  population?: string
}

export interface FunnelPrediction {
  thumbStop3s?: ThumbStop
  stages?: FunnelStage[]
  note?: string
}

export interface TopLists {
  wantedInfo?: string[]
  complaints?: string[]
  reactionKillers?: string[]
  highResponseSegments?: string[]
  lowResponseSegments?: string[]
}

export interface ExpertPanelItem {
  role: string
  finding?: string
  leverMapping?: string
  evidenceType?: ReferenceEvidenceType
}

export interface RedteamVote {
  role: string
  claim?: string
  /** 개방형(타당|조건부|약함 권장). */
  verdict?: string
  counterexample?: string
  verificationMethod?: string
  voteBasis?: string
  confidence?: number
}

export interface RiskAdjustedVerdict {
  summary?: string
  weightedConfidence?: number
  topRisks?: string[]
}

export interface AudienceDrift {
  currentAudience?: string
  intendedAudience?: string
  gap?: string
  priorityHint?: string
}

export interface ReferenceAnalysisSummary {
  promptVersion: ReferenceAnalysisPromptVersion
  simulatedPanelSize: ReferenceAnalysisPanelSize
  topScoreKey: ReferenceAnalysisScoreKey
  topScore: number
  topFactors: string[]
  extractedTopics: string[]
  createdAt: string
}

export type ReferenceAnalysisParseErrorCode =
  | 'PARSE_EMPTY'
  | 'PARSE_INVALID_JSON'
  | 'PARSE_INVALID_SCHEMA'
  | 'PARSE_UNSAFE_CLAIM'

export type InsightRegisterKind = 'structure' | 'idea' | 'topic'

// ============================================================================
// [P1-03] 스크립트 필터 상태 타입
// ============================================================================
export type ScriptSortOption = 'newest' | 'oldest' | 'name' | 'category'

export interface ScriptFilterState {
  search: string
  category: string
  sortBy: ScriptSortOption
}

// ============================================================================
// [P1-02] 차트 설정 타입
// ============================================================================
export type ChartMode = 'total' | 'individual'
export type ChartRange = '7d' | '30d' | 'all'
export type ChartScaleMode = 'combined' | 'split'

export interface ChartConfig {
  mode: ChartMode
  range: ChartRange
  scaleMode: ChartScaleMode
  selectedPostId?: string
}

// 차트 데이터셋 타입
export interface ChartDataset {
  label: string
  data: number[]
  borderColor: string
  backgroundColor: string
}

// ============================================================================
// [DP-01] 듀얼 패널 관련 타입
// ============================================================================

/** 듀얼 패널 선택 인덱스: 0 = 패널 1, 1 = 패널 2 */
export type PanelIndex = 0 | 1

/** 상세 패널 표시 모드 */
export type DetailPanelMode = 'read' | 'edit'
